import { useEffect, useState } from "react";
import { Loader2, Plus, Trash2, ArrowUp, ArrowDown, Search } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { estApi } from "../lib/api";

const money = (v) =>
  `$${Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const inputCls = "mt-1 h-8 w-full rounded-md border border-slate-200 px-2 text-xs";
const uid = () => (crypto.randomUUID ? crypto.randomUUID() : String(Math.random()));

export default function GroupDrawer({ open, onOpenChange, group, options, groups, onSaved }) {
  const [form, setForm] = useState({ name: "", description: "", category: "", notes: "", default_cost_code_id: "" });
  const [rows, setRows] = useState([]);          // { id, price_list_item_id|child_group_id, qty_multiplier, meta }
  const [search, setSearch] = useState("");
  const [results, setResults] = useState([]);
  const [saving, setSaving] = useState(false);
  const isNew = !group?.id;

  useEffect(() => {
    if (!open) return;
    setForm({
      name: group?.name || "", description: group?.description || "",
      category: group?.category || "", notes: group?.notes || "",
      default_cost_code_id: group?.default_cost_code_id || "",
    });
    setRows((group?.rows || []).map((r) => ({
      id: r.row_id || uid(),
      price_list_item_id: r.kind === "item" ? r.ref_id : null,
      child_group_id: r.kind === "group" ? r.ref_id : null,
      qty_multiplier: r.qty_multiplier,
      meta: r,
    })));
    setSearch("");
    setResults([]);
  }, [open, group]);

  useEffect(() => {
    if (!search.trim()) return setResults([]);
    const t = setTimeout(() => {
      estApi.getPriceList({ search, active: true, limit: 15 })
        .then((r) => setResults(r.items)).catch(() => {});
    }, 250);
    return () => clearTimeout(t);
  }, [search]);

  const addItem = (item) => {
    if (rows.some((r) => r.price_list_item_id === item.id)) return toast.info("Already in this group");
    setRows((rs) => [...rs, {
      id: uid(), price_list_item_id: item.id, child_group_id: null, qty_multiplier: 1,
      meta: {
        name: item.name, item_number: item.item_number, uom: item.uom,
        unit_cost: item.unit_cost, labor_hours_per_unit: item.labor_hours_per_unit, kind: "item",
      },
    }]);
    setSearch("");
    setResults([]);
  };

  const addChildGroup = (g) => {
    if (g.id === group?.id) return toast.error("A group cannot contain itself");
    if ((g.rows || []).some((r) => r.kind === "group")) {
      return toast.error(`"${g.name}" already contains a nested group — nesting is one level deep only`);
    }
    setRows((rs) => [...rs, {
      id: uid(), price_list_item_id: null, child_group_id: g.id, qty_multiplier: 1,
      meta: { name: g.name, item_number: "", uom: "", unit_cost: g.total_unit_cost,
              labor_hours_per_unit: g.total_labor_hours, kind: "group" },
    }]);
  };

  const move = (i, dir) => setRows((rs) => {
    const next = [...rs];
    const j = i + dir;
    if (j < 0 || j >= next.length) return rs;
    [next[i], next[j]] = [next[j], next[i]];
    return next;
  });

  const totals = rows.reduce((acc, r) => {
    const qty = parseFloat(r.qty_multiplier) || 0;
    acc.cost += (r.meta?.unit_cost || 0) * qty;
    acc.hours += (r.meta?.labor_hours_per_unit || 0) * qty;
    return acc;
  }, { cost: 0, hours: 0 });

  const payload = () => ({
    ...form,
    default_cost_code_id: form.default_cost_code_id || null,
    items: rows.map((r, i) => ({
      id: r.id, price_list_item_id: r.price_list_item_id, child_group_id: r.child_group_id,
      qty_multiplier: parseFloat(r.qty_multiplier) || 1, sort_order: i,
    })),
  });

  const save = async (asVersion) => {
    if (!form.name.trim()) return toast.error("Name is required");
    setSaving(true);
    try {
      const saved = isNew
        ? await estApi.createGroup(payload())
        : asVersion
          ? await estApi.newGroupVersion(group.id, payload())
          : await estApi.updateGroup(group.id, payload());
      toast.success(asVersion ? `Saved as version ${saved.version}` : isNew ? "Group created" : "Group saved");
      onSaved(saved);
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-[620px] sm:max-w-[620px] overflow-y-auto" data-testid="group-drawer">
        <SheetHeader>
          <SheetTitle>{isNew ? "New Group" : `Edit Group — v${group?.version || 1}`}</SheetTitle>
          <SheetDescription>
            A group is a named bundle of items with quantity multipliers. Added to an estimate it expands
            into individual lines so every line keeps its own budget code.
          </SheetDescription>
        </SheetHeader>

        <div className="mt-4 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="text-[11px] font-medium uppercase tracking-wide text-slate-500">Name</span>
              <input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                     className={inputCls} data-testid="group-name" />
            </label>
            <label className="block">
              <span className="text-[11px] font-medium uppercase tracking-wide text-slate-500">Category</span>
              <input value={form.category} onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))}
                     className={inputCls} data-testid="group-category" />
            </label>
          </div>
          <label className="block">
            <span className="text-[11px] font-medium uppercase tracking-wide text-slate-500">Description</span>
            <input value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                   className={inputCls} data-testid="group-description" />
          </label>
          <label className="block">
            <span className="text-[11px] font-medium uppercase tracking-wide text-slate-500">Default cost code</span>
            <select value={form.default_cost_code_id}
                    onChange={(e) => setForm((f) => ({ ...f, default_cost_code_id: e.target.value }))}
                    className={inputCls} data-testid="group-cost-code">
              <option value="">— none —</option>
              {(options?.cost_codes || []).map((c) => (
                <option key={c.id} value={c.id}>{c.code} {c.name}</option>
              ))}
            </select>
          </label>

          {/* Item search */}
          <div className="rounded-md border border-slate-200 p-2">
            <div className="relative">
              <Search className="h-3.5 w-3.5 text-slate-400 absolute left-2 top-1/2 -translate-y-1/2" />
              <input value={search} onChange={(e) => setSearch(e.target.value)}
                     placeholder="Search..."
                     className="h-8 w-full rounded-md border border-slate-200 pl-7 pr-2 text-xs"
                     data-testid="group-item-search" />
            </div>
            {results.length > 0 && (
              <div className="mt-1 max-h-40 overflow-auto" data-testid="group-search-results">
                {results.map((r) => (
                  <button key={r.id} onClick={() => addItem(r)}
                          className="w-full flex items-center gap-2 rounded px-2 py-1 text-left text-[11px] hover:bg-slate-100"
                          data-testid={`group-add-item-${r.id}`}>
                    <Plus className="h-3 w-3 text-slate-400" />
                    <span className="font-mono text-slate-500 w-20 truncate">{r.item_number || "—"}</span>
                    <span className="flex-1 truncate text-slate-700">{r.name}</span>
                    <span className="text-slate-500">{money(r.unit_cost)}</span>
                  </button>
                ))}
              </div>
            )}
            {groups?.length > 0 && (
              <details className="mt-2">
                <summary className="cursor-pointer text-[11px] text-slate-500">
                  Add a nested group (one level deep)
                </summary>
                <div className="mt-1 max-h-32 overflow-auto">
                  {groups.filter((g) => g.id !== group?.id).map((g) => (
                    <button key={g.id} onClick={() => addChildGroup(g)}
                            className="w-full flex items-center gap-2 rounded px-2 py-1 text-left text-[11px] hover:bg-slate-100"
                            data-testid={`group-add-child-${g.id}`}>
                      <Plus className="h-3 w-3 text-slate-400" />
                      <span className="flex-1 truncate text-slate-700">{g.name}</span>
                      <span className="text-slate-500">{money(g.total_unit_cost)}</span>
                    </button>
                  ))}
                </div>
              </details>
            )}
          </div>

          {/* Items table */}
          <div className="rounded-md border border-slate-200 overflow-hidden">
            <table className="w-full text-[11px]" data-testid="group-items-table">
              <thead className="bg-slate-50">
                <tr>
                  {["", "Item", "Qty", "Unit cost", "Hrs", "Ext cost", ""].map((h, i) => (
                    <th key={i} className="px-2 py-1.5 text-left font-medium text-slate-500">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 && (
                  <tr><td colSpan={7} className="px-2 py-6 text-center text-slate-400">
                    No items yet — search above to add some.
                  </td></tr>
                )}
                {rows.map((r, i) => (
                  <tr key={r.id} className="border-t border-slate-100" data-testid={`group-row-${r.id}`}>
                    <td className="px-1 py-1">
                      <div className="flex flex-col">
                        <button onClick={() => move(i, -1)} className="text-slate-300 hover:text-slate-600">
                          <ArrowUp className="h-3 w-3" />
                        </button>
                        <button onClick={() => move(i, 1)} className="text-slate-300 hover:text-slate-600">
                          <ArrowDown className="h-3 w-3" />
                        </button>
                      </div>
                    </td>
                    <td className="px-2 py-1">
                      <div className="truncate max-w-[220px] text-slate-700" title={r.meta?.name}>
                        {r.meta?.kind === "group" && (
                          <span className="mr-1 rounded bg-blue-100 px-1 text-[9px] text-blue-700">group</span>
                        )}
                        {r.meta?.name}
                      </div>
                      <div className="font-mono text-[10px] text-slate-400">{r.meta?.item_number}</div>
                    </td>
                    <td className="px-2 py-1">
                      <input type="number" step="0.01" value={r.qty_multiplier}
                             onChange={(e) => setRows((rs) => rs.map((x) =>
                               x.id === r.id ? { ...x, qty_multiplier: e.target.value } : x))}
                             className="h-6 w-16 rounded border border-slate-200 px-1 text-right"
                             data-testid={`group-qty-${r.id}`} />
                    </td>
                    <td className="px-2 py-1 text-slate-600">{money(r.meta?.unit_cost)}</td>
                    <td className="px-2 py-1 text-slate-600">{r.meta?.labor_hours_per_unit || 0}</td>
                    <td className="px-2 py-1 text-slate-800">
                      {money((r.meta?.unit_cost || 0) * (parseFloat(r.qty_multiplier) || 0))}
                    </td>
                    <td className="px-1 py-1">
                      <button onClick={() => setRows((rs) => rs.filter((x) => x.id !== r.id))}
                              className="text-slate-300 hover:text-red-500"
                              data-testid={`group-remove-${r.id}`}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="grid grid-cols-3 gap-2" data-testid="group-rollup">
            {[["Items", rows.length], ["Total unit cost", money(totals.cost)],
              ["Total labor hrs", totals.hours.toFixed(2)]].map(([l, v]) => (
              <div key={l} className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2">
                <div className="text-[10px] uppercase tracking-wide text-slate-500">{l}</div>
                <div className="text-sm font-semibold text-slate-800">{v}</div>
              </div>
            ))}
          </div>

          <div className="flex justify-end gap-2 pt-2 pb-6">
            <Button variant="outline" size="sm" onClick={() => onOpenChange(false)}
                    data-testid="group-cancel-btn">Cancel</Button>
            {!isNew && (
              <Button variant="outline" size="sm" onClick={() => save(true)} disabled={saving}
                      data-testid="group-save-version-btn">
                Save as new version
              </Button>
            )}
            <Button size="sm" onClick={() => save(false)} disabled={saving} data-testid="group-save-btn">
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {isNew ? "Create group" : "Save group"}
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
